<template>
    <div v-if="config.display" :style="{'width': config.width || '100%'}" class="control e-control" :id="config.id">
        <label-wrapper :label-settings="config"></label-wrapper>
        <div v-if="!showAsDefaultInput" class="drop-area" :id="'dropArea'+config.id">
            <div class="icon fl">
                <div class="icon-image"></div>
            </div>
            <div class="text fl">
                <div id="dropText"> Drag and Drop or <a @click="click">choose a file</a> <span v-if="config.entityName">to upload your {{config.entityName}}</span></div>
                <div v-if="config.showExtensionsText && config.allowedExtensions" id="extentionText">
                    {{config.allowedExtensionsText || config.allowedExtensions}} file types are supported. {{config.allowedExtensions && config.allowedExtensions.indexOf('xlsx') !== -1 ? 'But xlsx is recommended.' : ''}}
                </div>
            </div>
            <div class="clearfix"></div>
            <ejs-uploader
                v-bind="localSyncfusionConfig"
                :ref="config.ref || config.id"
                :autoUpload="false"
                :dropArea="'#dropArea'+config.id"
                :selected="selected"
                :clearing="clearing"
                :removing="removing"
                :multiple="multiple"
            ><slot></slot></ejs-uploader>
        </div>
        <div v-else>
            <div v-if="config.recordId && config.psp && !isUdfAttachment && !uploadNew && !replaceAttachment && !selectedAttachment">
                <control v-if="config.psp === 'component'" :name="'ATTACH_ID__' + config.id" :prop-config="controlConfigs.attachIdComponentConfig" :value="attachIdValue" @update:value="attachIdValue = $event"></control>
                <control v-else :name="'ATTACH_ID__' + config.id" :prop-config="controlConfigs.attachIdConfig" :value="attachIdValue" @update:value="attachIdValue = $event"></control>
            </div>
            <div class="flex">
                <div v-if="attachIdUrl && !showCancelBtn" class="flex-6 preview">
                    <div v-if="attachIdIsImage" class="preview-inner">
                        <img :src="attachIdUrl" alt="Preview"/>
                    </div>
                    <div v-else>
                        File Uploaded!<br/>
                        <a @click="copyUrl">Copy Url</a> | <a @click="download">Download this file</a>
                    </div>
                    <a ref="downloadLink" :href="attachIdUrl + '&d=1'" v-show="false">Link</a>
                </div>
                <div :class="{'flex-10': attachIdValue, 'flex-6': !attachIdValue}">
                    <div v-if="replaceAttachment || uploadNew">
                        <p v-if="uploadMessage" class="upload-message"><strong>{{uploadMessage}}</strong></p>
                        <ejs-uploader
                            v-bind="localSyncfusionConfig"
                            :ref="config.ref || config.id"
                            :autoUpload="false"
                            :selected="selected"
                            :clearing="clearing"
                            :removing="removing"
                            :multiple="multiple"
                            :beforeUpload="beforeUpload"
                        ><slot></slot></ejs-uploader>
                    </div>
                    <div style="display: flex; justify-content: end;">
                        <div style="text-align: right;">
                            <a class="attach-action-btn" v-if="showDeleteBtn" @click="deleteAttachment">Delete</a>
                            <a class="attach-action-btn" v-if="showReplaceBtn" @click="replaceAttachment = true">Replace</a>
                            <a class="attach-action-btn upload-new" v-if="showNewBtn" @click="uploadNew = true">Upload New</a>
                            <a class="attach-action-btn" v-if="showCancelBtn" @click="replaceAttachment = false; uploadNew = false;">Choose Existing Attachment</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/* eslint-disable max-len */
import { mapGetters } from "vuex";
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { recordStore } from "@common/recordStore";
import { configStore } from "@common/configStore";
import { library } from "@common/library/library";
import { controlMixins } from "./controlMixins.js";

const controlComponent = defineAsyncComponent(() => import('@common/control/control.vue'));

export const uploadFileComponent = {
    data() {
        return {
            attachIdValue: false,
            replaceAttachment: false,
            uploadNew: false
        };
    },
    components: {
        control: controlComponent,
        'ejs-uploader': defineAsyncComponent(() => import('@common/syncfusion/uploader'))
    },
    created() {
        configStore.getDevDashLocalInstanceDetails();
    },
    mixins: [controlMixins],
    computed: {
        ...mapGetters({
            attachTypeListOptions: 'configStore/attachTypeListOptions'
        }),
        localSyncfusionConfig() {
            let config = library.cloneObj(this.config);
            delete config.type;
            delete config.multiple;
            delete config.value;
            delete config.valid;
            return config;
        },
        componentAttachments() {
            return this.attachTypeListOptions && this.attachTypeListOptions[this.config.recordId];
        },
        isUdfAttachment() {
            return this.config.name.indexOf('CA#') === 0;
        },
        selectedAttachment() {
            // Make sure we have all our data
            if (!this.componentAttachments || (!this.value && !this.attachIdValue)) return null;
            // Clear the value and return no selectedAttachment if dropdown was cleared (attachIdValue initializes as false, and will be null when cleared)
            if (this.attachIdValue === null) {
                this.value = false;
                return null;
            }
            let selected = null;
            if (this.attachIdValue) selected = this.componentAttachments.find((item) => item.additionalData.ATTACH_ID === Number(this.attachIdValue));
            else if (this.value) selected = this.componentAttachments.find((item) => item.additionalData.fullUrl === this.value);
            if (selected) {
                this.attachIdValue = selected.value;
                this.value = selected.additionalData.fullUrl;
            }
            return selected;
        },
        controlConfigs() {
            let itemsSourceString = 'configStore.getAttachTypeListOptions("' + this.config.recordId + '", "' + this.config.psp + '")';
            return {
                attachIdConfig: {
                    name: 'ATTACH_ID',
                    type: 'dropdown',
                    placeholder: 'Select an existing attachment or upload a new one',
                    label: 'Please select Attachment that should be updated',
                    hideLabel: true,
                    itemsSource: itemsSourceString,
                    fields: { text: 'text', value: 'value' }
                },
                attachIdComponentConfig: {
                    name: 'ATTACH_ID',
                    type: 'dropdown',
                    placeholder: 'Select an existing attachment or upload a new one',
                    label: 'Please select Attachment or upload a new one',
                    hideLabel: true,
                    itemsSource: itemsSourceString,
                    fields: { text: 'text', value: 'value' }
                }
            };
        },
        showAsDefaultInput() {
            let showAsDefaultInput = true;
            if (!(this.config.recordId && this.config.psp) && typeof this.config.showAsDefaultInput !== 'undefined') {
                showAsDefaultInput = this.config.showAsDefaultInput;
            }
            return showAsDefaultInput;
        },
        multiple() {
            return !this.config.recordId
                && !this.config.psp
                && typeof this.config.multiple !== 'undefined'
                && this.config.multiple;
        },
        attachIdUrl() {
            let imageUrl;
            if (this.value && this.isUdfAttachment) return this.value;
            if (this.config.psp && this.config.psp !== 'component' && this.config.recordId && this.attachIdValue) {
                let recordId = this.config.recordId;
                let fields = ['ATTACHURL#' + this.attachIdValue];
                this.recordStore.subscribe({ source: ['er_' + this.config.psp + 'Fields'], params: { recordId, fields } }, this.config.name);
                imageUrl = this.recordStore.records[this.config.psp][this.config.recordId]['ATTACHURL#' + this.attachIdValue];
            } else if (this.selectedAttachment) {
                imageUrl = this.selectedAttachment.additionalData.fullUrl;
            }
            if (typeof VUE_PROJECT_NAME !== 'undefined'
                && VUE_PROJECT_NAME === 'devDash'
                && this.configStore
                && this.configStore.devDashLocalInstanceDetails
                && this.configStore.devDashLocalInstanceDetails.url
                && imageUrl
                && imageUrl.indexOf(this.configStore.devDashLocalInstanceDetails.url) !== -1
            ) {
                imageUrl = imageUrl.replace(this.configStore.devDashLocalInstanceDetails.url, PROJECTS_URL);
                if (this.configStore.devDashLocalInstanceDetails.devDashSID) imageUrl += '&PHPSESSID=' + this.configStore.devDashLocalInstanceDetails.devDashSID;
            }
            return imageUrl;
        },
        attachIdIsImage() {
            let attachFilename = this.selectedAttachment
                && this.selectedAttachment.additionalData
                && this.selectedAttachment.additionalData.MAC_FILE_NAME
                && this.selectedAttachment.additionalData.MAC_FILE_NAME.toLowerCase();
            return !!(attachFilename
                && (
                    attachFilename.indexOf('jpeg') !== -1
                    || attachFilename.indexOf('jpg') !== -1
                    || attachFilename.indexOf('png') !== -1
                ));
        },
        showDeleteBtn() {
            return ((this.isUdfAttachment && this.value) || this.attachIdValue) && !this.replaceAttachment && !this.uploadNew;
        },
        showReplaceBtn() {
            return ((this.isUdfAttachment && this.value) || this.attachIdValue) && !this.replaceAttachment && !this.uploadNew;
        },
        showNewBtn() {
            return !this.replaceAttachment && !this.uploadNew;
        },
        showCancelBtn() {
            return this.replaceAttachment || this.uploadNew;
        },
        uploadMessage() {
            if (this.uploadNew) return 'Choose a new attachment to upload from your device.';
            if (this.replaceAttachment) return 'Choose a replacement attachment to upload from your device.';
            return false;
        }
    },
    methods: {
        /* istanbul ignore next */
        click() {
            document.querySelector('#' + this.config.id + ' .e-file-select-wrap button').click();
        },
        async selected(event) {
            if (!event.filesData) return;
            if (!Array.isArray(this.valueFiles)) this.valueFiles = [];
            for (let i in event.filesData) {
                if (i === 'length') continue;
                if (event.filesData[i].statusCode === '0') continue;
                // eslint-disable-next-line no-await-in-loop
                let base64 = await library.fileToBase64(event.filesData[i].rawFile);
                if (this.config.psp !== 'component') {
                    if (!Array.isArray(this.value)) this.value = [];
                    this.value.push({
                        id: event.filesData[i].id,
                        name: event.filesData[i].name,
                        base64: base64
                    });
                }
                this.valueFiles.push({
                    id: event.filesData[i].id,
                    name: event.filesData[i].name,
                    base64: base64
                });
            }
            this.$emit('update:valueFiles', this.valueFiles);
        },
        clearing() {
            this.valueFiles = [];
            this.$emit('update:valueFiles', this.valueFiles);
        },
        removing(event) {
            if (this.config.showRemoveConfirmation) {
                /* istanbul ignore next */
                event.cancel = true;
                library.showDialog('Are you sure you want to remove this file?', () => {
                    let ref = this.config.ref || this.config.id;
                    for (let i in event.filesData) {
                        if (i === 'length') continue;
                        this.$refs[ref].remove(event.filesData[i], false, true);
                    }
                    this.remove(event);
                });
            } else {
                this.remove(event);
            }
        },
        remove(event) {
            if (event.filesData) {
                let items = library.cloneObj(this.valueFiles);
                for (let i in event.filesData) {
                    if (i === 'length') continue;
                    let index = items.findIndex((obj) => obj.id === event.filesData[i].id);
                    if (index >= 0) items.splice(index, 1);
                }
                this.valueFiles = items;
                this.$emit('update:valueFiles', this.valueFiles);
            }
        },
        deleteAttachment() {
            // er_deleteAttach(attTypeId, otherName, recordId, abbrev='vol')
            let performDelete = (attachId) => {
                let psp = this.config.psp;
                let recordId = this.config.recordId;
                if (this.isUdfAttachment) {
                    let fields = {};
                    fields[attachId] = false;
                    sDs.post('sds_updateProfileField', psp, recordId, fields).then((response) => {
                        if (response && response.status === 'success') {
                            this.value = null;
                        }
                    });
                } else {
                    sDs.post('er_deleteAttach', attachId, false, recordId, psp).then((response) => {
                        if (response && response.status === 'success' && psp === 'component') {
                            configStore.getAttachTypeListOptions(recordId, psp, true)
                                .then(() => {
                                    this.attachIdValue = null;
                                });
                        }
                    });
                }
            };
            let options = {
                'name': 'deleteConfirm',
                'okButtonLabel': 'Delete',
                'cancelButtonLabel': 'Cancel',
                'title': 'Delete Attachment'
            };
            library.showDialog('<p style="font-size:14px;"><strong>Note: Deleting an attachment will not change the component\'s configuration.</strong></p>If you want to change the component configuration, you must also choose a new attachment option to save, or save the empty selection.  Do you want to continue?',
                () => {
                    performDelete(this.attachIdValue);
                },
                () => {
                    library.showToast('Delete cancelled', { type: 'warning' });
                },
                options);
        },
        async upload(isNew) {
            let psp = this.config.psp;
            let recordId = this.config.recordId;
            let attachID = psp === 'component' && (isNew || !this.attachIdValue) ? -1 : this.attachIdValue;
            let options = { title: 'Uploading File', okButtonLabel: 'Ok', showAsAlert: true };
            library.showDialog('<strong>Note:</strong> Your attachment is being uploaded.  After the upload is complete, you must also save the component configuration with your new attachment selected.', false, false, options);
            let fields = {};
            fields[this.config.name] = { fname: this.valueFiles[0].name, fileContent: this.valueFiles[0].base64 };
            if (this.isUdfAttachment) {
                sDs.post('sds_updateProfileField', psp, recordId, fields).then((response) => {
                    if (response && response.status === 'success' && psp === 'component') {
                        // Update the list of attachments
                        configStore.getAttachTypeListOptions(recordId, psp, true).then(() => {
                            this.replaceAttachment = false;
                            this.uploadNew = false;
                        });
                    }
                });
            }
            else {
                sDs.post('er_uploadAttach', attachID, psp, recordId, this.valueFiles[0].name, this.valueFiles[0].base64).then((response) => {
                    if (response && response.status === 'success' && psp === 'component') {
                        // Update the list of attachments
                        configStore.getAttachTypeListOptions(recordId, psp, true).then(() => {
                            if (attachID === -1) {
                                // Get the new attachment ID from the list of component attachments
                                let newIndex = this.componentAttachments.length - 1;
                                this.attachIdValue = this.componentAttachments[newIndex].value;
                                this.replaceAttachment = false;
                                this.uploadNew = false;
                            }
                        });
                    }
                });
            }
        },
        beforeUpload() {
            let psp = this.config.psp;
            if (this.config.recordId && psp) {
                if (!this.isUdfAttachment && !this.attachIdValue && psp !== 'component') {
                    library.showDialog('Please select Attachment that should be changed', false, false, { showAsAlert: true });
                    return;
                }
                if (!this.valueFiles.length) {
                    library.showDialog('Please select the file', false, false, { showAsAlert: true });
                    return;
                }
                let attachId = !this.isUdfAttachment && this.componentAttachments.find((item) => item.value === this.attachIdValue);
                let options = { 'name': 'attachConfirm', 'okButtonLabel': 'Rewrite', 'cancelButtonLabel': 'Cancel' };
                if (psp === 'component') options['cancelButtonLabel'] = 'Upload as New';
                if (this.replaceAttachment && attachId && attachId.additionalData && attachId.additionalData.MAC_FILE_NAME) {
                    library.showDialog('You are about to overwrite the existing attachment.  Do you want to continue?',
                        () => {
                            this.upload();
                        },
                        () => {
                            library.showToast('Upload cancelled', { type: 'warning' });
                        },
                        options);
                } else {
                    this.upload(true);
                }
            }
        },
        download() {
            this.$refs['downloadLink'].click();
        },
        copyUrl() {
            library.copyTextToClipboard(this.attachIdUrl);
        }
    },
    unmounted() {
        if (this.recordStore && typeof this.recordStore.unsubscribe === 'function') {
            this.recordStore.unsubscribe(false, this.config.name);
        }
    }
};

export default uploadFileComponent;
</script>

<style scoped>
:deep(.drop-area .e-file-select-wrap), :deep(.drop-area .e-upload-actions)  {
    display: none;
}

:deep(.drop-area .e-upload) {
    border: none;
}

.drop-area {
    min-height: 80px;
    border: 2px dashed #92a3b6;
    border-radius: 10px;
    background-color: #f3f7f9;
    padding: 20px 20px 0px 20px;
    font-size: 14px;
}

.drop-area #browse {
    text-decoration: none;
    color: #0378d5;
}

.drop-area .fl {
    float: left;
}

.drop-area .icon {
    width: 40px;
    min-width: 50px;
    min-height: 50px;
}
.drop-area .text {
    margin-bottom: 20px;
}
.icon-image {
    width: 40px;
    height: 40px;
    background-size: cover;
    background-image:url(data:image/png;base64,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);
}

.flex {
    display: flex;
}

.flex-4 {
    flex-grow: 4;
    /* max-width: 200px; */
    overflow: hidden;
    cursor: pointer;
    align-items: center;
    justify-content: center;
}

.flex-4 img {
    width: 100%;
}

a {
    cursor: pointer;
}

.flex-6 {
    flex-grow: 6;
}

.flex-10 {
    flex-grow: 10;
}

.preview {
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-top-color: rgba(0, 0, 0, 0.12);
    border-top-style: solid;
    border-top-width: 1px;
    border-right-color: rgba(0, 0, 0, 0.12);
    border-right-style: solid;
    border-right-width: 1px;
    border-bottom-color: rgba(0, 0, 0, 0.12);
    border-bottom-style: solid;
    border-bottom-width: 1px;
    border-left-color: rgba(0, 0, 0, 0.12);
    border-left-style: solid;
    border-left-width: 1px;
    margin-right: 10px;
    font-size: 16px;
}
.preview-inner {
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
}
.upload-message {
    flex: 1;
    padding: 4px;
    margin: 0;
    font-size: 14px;
}
.attach-action-btn {
    display: inline-block;
    padding: 0 8px;
    margin-top: 2px;
    border-right: 1px solid #ccc;
    font-size: 14px;
}
.attach-action-btn:last-child {
    border-right: 0 none;
}
</style>
